import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import type { GenericList } from './models/JobSharedInterfaces';
import JobTabsData from './JobTabs';
import Pager from './Paging';
import { GetQuery, PostQuery } from './Shared';

interface NoteList {
    id: number,
    type: string,
    item: string,
    dateFormatted: string,
    notes: string,
    addedBy: string,
    systemNote: boolean
}

const JobNoteData = () => {
    const [loading, setLoading] = React.useState(true);
    let navigate = useNavigate();
    const { id } = useParams();
    const [siteAddress, setSiteAddress] = React.useState("");
    const [noteTypeId, setNoteTypeId] = React.useState(-1);
    const [newNotes, setNewNotes] = React.useState("");
    const [showSystemNotes, setShowSystemNotes] = React.useState(false);
    const [showUserNotes, setShowUserNotes] = React.useState(false);
    const [noteTypeFilter, setNoteTypeFilter] = React.useState(-1);
    const [noteItemFilter, setNoteItemFilter] = React.useState(-1);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [getTotalRecords, setGetTotalRecords] = React.useState(true);
    const [getNoteList, setGetNoteList] = React.useState(false);
    const [jobNotes, setJobNotes] = React.useState<NoteList[]>([]);
    const [noteTypes, setNoteTypes] = React.useState<GenericList[]>([]);
    const [noteItems, setNoteItems] = React.useState<GenericList[]>([]);
    const [showAddNote, setShowAddNote] = React.useState(false);
    const [gridLoading, setGridLoading] = React.useState(true);
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    
    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    const getData = async () => {
        const apiUrl = 'GetNoteDropdowns?JobId=' + id;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setSiteAddress(result.data.siteAddress);
            setNoteTypes(result.data.noteTypes);
            setNoteItems(result.data.noteItems);
            getNotes();
        }
    }

    React.useEffect(() => {
        if (getNoteList) {
            setGridLoading(true);
            getNotes();
        }
    }, [getNoteList]);

    const getNotes = async () => {
        var totalNotes = getTotalRecords ? 0 : totalRecords;

        const apiUrl = 'GetNotes?jobId=' + id + '&page=' + page + '&totalRecords=' + totalNotes + '&getTotalRecords=' + getTotalRecords + '&showSystemNotes=' + showSystemNotes + '&showUserNotes=' + showUserNotes + '&noteType=' + noteTypeFilter + '&noteItemId=' + noteItemFilter;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setPageSize(result.data.pageSize);
            setTotalRecords(result.data.totalRecords);
            setJobNotes(result.data.jobNotes);
            setLoading(false);
            setGridLoading(false);
            setGetNoteList(false);
        }
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (noteTypeId <= 0) {
            formIsValid = false;
            errorList["noteTypeId"] = "Note Type is required";
        }

        if (!newNotes || newNotes.length < 5) {
            formIsValid = false;
            errorList["newNotes"] = "Notes > 5 characters are required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveNote = (e: any) => {
        e.preventDefault();

        if (saveDisabled) {
            return;
        }

        if (validate()) {
            setSaveDisabled(true);
            saveJobNote();
        } else {
            toast.error("Fix validation errors before saving note!");
        }
    }

    const saveJobNote = async () => {
        let saveJobNote = {
            jobId: id,
            noteTypeId: noteTypeId,
            note: newNotes
        }
        var jsonItem = JSON.stringify(saveJobNote);

        const apiUrl = "SaveJobNote";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            toast.success("Note Saved");
            setSaveDisabled(false);

            if (page == 1) {
                var newNote = {
                    id: result.data.id,
                    type: result.data.noteType,
                    item: "",
                    dateFormatted: result.data.dateFormatted,
                    notes: result.data.note,
                    addedBy: result.data.addedBy,
                    systemNote: result.data.systemNote
                };

                //add note to top of list
                var pageNotes = [...jobNotes];
                pageNotes.unshift(newNote);
                if (pageNotes.length > (pageSize)) {
                    //remove the oldest note as goes onto 2nd page
                    pageNotes.pop();
                }
                setJobNotes(pageNotes);
            }
            setNoteTypeId(-1);
            setNewNotes("");
            setShowAddNote(false);
        }
    }

    const clearError = (e: any) => {
        var error = errors;
        if (e.target.value) {
            delete error[e.target.name];
            setErrors(error);
        }
        setSaveDisabled(false);
    }

    const handleNoteTypeChange = (e: any) => {
        clearError(e);
        setNoteTypeId(e.target.value * 1);
    }

    const handleNotesChange = (e: any) => {
        clearError(e);
        setNewNotes(e.target.value);
    }

    const handleSysNotesChange = (e: any) => {
        setShowSystemNotes(e.target.checked);
        setShowUserNotes(false);
        setPage(1);
        setGetTotalRecords(true);
        setGetNoteList(true);
    }

    const handleUserNotesChange = (e: any) => {
        setShowUserNotes(e.target.checked);
        setShowSystemNotes(false);  //reset system notes if changing user ones
        setPage(1);
        setGetTotalRecords(true);
        setGetNoteList(true);
    }

    const handleTypeChange = (e: any) => {
        setNoteTypeFilter(e.target.value * 1);
        setPage(1);
        setGetTotalRecords(true);
        setGetNoteList(true);
    }

    const handleItemChange = (e: any) => {
        setNoteItemFilter(e.target.value * 1);
        setPage(1);
        setGetTotalRecords(true);
        setGetNoteList(true);
    }

    const pageChanged = (newPage: number) => {
        setPage(newPage);
        setGetTotalRecords(false);
        setGetNoteList(true);
    }

    const showAddNewNote = () => {
        setShowAddNote(!showAddNote);
    }

    const tabChanged = (url: string) => {
        navigate(url);
    }

    var pagerContents;
    if ((totalRecords / pageSize ) > 1) {
        //only render if we have records
        pagerContents = <Pager page={page} pageSize={pageSize} totalRecords={totalRecords} pageChanged={pageChanged} />
    }

    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"notes"} tabChanged={tabChanged} />;

    const renderDetails = (
        <div>
            {jobTab}
            <div className={showAddNote ? "hidden" : ""}>
                <button className="defaultbutton defaultbutton__small" onClick={showAddNewNote} type="button">Add Note</button>
            </div>
            <div className={showAddNote ? "" : "hidden" }>
                <label className="input-group" htmlFor="noteType">
                    <span className="label">Type </span>
                    <select className="select" id="noteType" name="noteTypeId" value={noteTypeId} onChange={(e) => handleNoteTypeChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {noteTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["noteTypeId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["noteTypeId"]}</span>

                <label className="input-group" htmlFor="newNotes">
                    <span className="label">Notes</span>
                    <textarea className="input job__noteSize" id="newNotes" name="newNotes" value={newNotes} onChange={(e) => handleNotesChange(e)}></textarea>     
                </label>
                <span className={errors["newNotes"] ? "label errors marginLeftLabel" : "hidden"}>{errors["newNotes"]}</span>
                
                <div className="marginTop10">
                    <button className="defaultbutton defaultbutton-prev" onClick={saveNote} type="button">Add</button>
                    <button className="defaultbutton defaultbutton-next" onClick={showAddNewNote} type="button">Cancel</button>
                </div>
            </div>
            <div>
                <hr></hr>
            </div>
            <div className="makeitflex">
                <label className="input-group--inline" htmlFor="noteTypeFilter">
                    <span className="label">Type </span>
                    <select className="select" id="noteTypeFilter" name="noteTypeFilter" value={noteTypeFilter} onChange={(e) => handleTypeChange(e)}>
                        <option defaultValue="-1"></option>
                        {noteTypes.map(type =>
                            <option key={type.id} value={type.id}>{type.name}</option>
                        )};
                    </select>
                </label>
                <label className="input-group--inline" htmlFor="noteItemFilter">
                    <span className="label">Item </span>
                    <select className="select" id="noteItemFilter" name="noteItemFilter" value={noteItemFilter} onChange={(e) => handleItemChange(e)}>
                        <option defaultValue="-1"></option>
                        {noteItems.map(item =>
                            <option key={item.id} value={item.id}>{item.name}</option>
                        )};
                    </select>
                </label>
                <label className="input-group--inline job__checkboxPosition" htmlFor="showSystemNotes">
                    <span className="label">Show System Notes </span>
                    <div>
                        <input type="checkbox" name="showSystemNotes" id="showSystemNotes" checked={showSystemNotes}  onChange={(e) => handleSysNotesChange(e)}></input>
                    </div>
                </label>
                <label className="input-group--inline job__checkboxPosition" htmlFor="showUserNotes">
                    <span className="label">Show User Notes </span>
                    <div>
                        <input type="checkbox" name="showUserNotes" id="showUserNotes" checked={showUserNotes} onChange={(e) => handleUserNotesChange(e)}></input>
                    </div>
                </label>
            </div>

            <div className={gridLoading ? "" : "hidden" }>Loading Notes...</div>
            <div className="job__noteGridSize overflowAuto">
                <table className="table tableColours">
                    <thead>
                        <tr>
                            <th>Date/Time</th>
                            <th>Type</th>
                            <th>Item</th>
                            <th>Notes</th>
                            <th>Added By</th>
                        </tr>
                    </thead>
                    {jobNotes.map(note =>
                        <tbody key={note.id}>
                            <tr>
                                <td>{note.dateFormatted}</td>
                                <td>{note.type}</td>
                                <td>{note.item}</td>
                                <td>{note.notes}</td>
                                <td>{note.addedBy}</td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
            {pagerContents}
        </div>    
    )

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobNoteData;